import { useState, useEffect } from 'react';
import { ArrowRight, Shield, Award, Users, Clock, FileCheck, CheckCircle, Zap, Star, ChevronDown, MessageCircle, Search, FileText, TrendingUp } from 'lucide-react';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import ContactPopup from '@/components/ContactPopup';
import PricingTable from '@/components/PricingTable';
import MobileSlider from '@/components/MobileSlider';
import { fetchGeolocation, getDialCodeFromCountry, submitLead, handleLeadResponse } from '@/services/leadService';
import { openChat } from '@/utils/chatService';

const LandingPage = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+1',
    phone: '',
    message: ''
  });

  // Fetch geolocation on mount
  useEffect(() => {
    const loadGeoData = async () => {
      const data = await fetchGeolocation();
      setGeoData(data);
      
      // Auto-populate country code from geolocation
      if (data?.data?.countryCode) {
        const dialCode = getDialCodeFromCountry(data.data.countryCode);
        const formattedDialCode = `+${dialCode}`;
        setFormData(prev => ({ ...prev, country_code: formattedDialCode }));
      }
    };
    loadGeoData();
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numbersOnly = value.replace(/\D/g, '');
    const limitedValue = numbersOnly.slice(0, 10);
    
    e.target.value = limitedValue;
    
    if (value !== numbersOnly) {
      setPhoneError('Only numbers are allowed');
    } else if (numbersOnly.length > 10) {
      setPhoneError('Maximum 10 digits allowed');
    } else if (numbersOnly.length > 0 && numbersOnly.length < 10) {
      setPhoneError('Phone number must be 10 digits');
    } else {
      setPhoneError('');
    }
  };

  return (
    <>
      <SEO 
        title="Register Your Trademark Today - Fast & Affordable Protection"
        description="Protect your brand with professional trademark registration. Expert attorneys, 98% success rate, affordable pricing. Get started today with free consultation and fast turnaround."
        keywords="register trademark, trademark registration service, trademark protection, brand registration, trademark filing"
      />
      
      <div className="min-h-screen bg-white">
        {/* Header - Simplified, No Links */}
        <header className="bg-white shadow-md sticky top-0 z-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-20">
              <img 
                src="/logo-black.png" 
                alt="Trademark Scribe" 
                className="h-12 w-auto"
              />
              <div className="flex items-center space-x-4">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all"
                >
                  Register My Trademark
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Creative Background */}
        <section className="relative py-12 md:py-20 overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900">
          {/* Animated Background Elements */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-96 h-96 bg-red-600/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-purple-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(239,68,68,0.15),transparent_40%),radial-gradient(circle_at_80%_70%,rgba(249,115,22,0.15),transparent_40%)]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:3rem_3rem]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left Side - Content */}
              <div className="text-white space-y-6">
                <div className="inline-block animate-bounce">
                  <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide shadow-lg">
                    ⚡ 5,000+ Successful Registrations
                  </span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  <span className="block">Secure Your Brand's</span>
                  <span className="block mt-2 bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent animate-pulse">
                    Legal Foundation Today
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-200 leading-relaxed">
                  Expert trademark registration services from certified attorneys. Industry-leading 98% approval rate, streamlined process, transparent pricing.
                </p>

                <div className="flex flex-wrap gap-2 pt-2">
                  <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/20 transition-all">
                    <Shield className="w-4 h-4 text-red-400" />
                    <span className="text-xs font-medium">USPTO Certified</span>
                  </div>
                  <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/20 transition-all">
                    <Award className="w-4 h-4 text-orange-400" />
                    <span className="text-xs font-medium">Award Winning</span>
                  </div>
                  <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/30 hover:bg-white/20 transition-all">
                    <Users className="w-4 h-4 text-red-400" />
                    <span className="text-xs font-medium">Trusted Nationwide</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Form */}
              <div className="bg-white rounded-2xl p-6 shadow-2xl border border-gray-100">
                <div className="mb-5">
                  <div className="inline-block mb-2">
                    <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                      🔥 Act Now
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold mb-1 text-gray-900 leading-tight">
                    Protect Your Brand Before It's Too Late!
                  </h2>
                  <p className="text-sm text-gray-600">
                    <strong className="text-red-600">Free consultation</strong> · Expert guidance · 24-hour response
                  </p>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setIsSubmitting(true);
                  setSubmitError('');
                  setPhoneError('');

                  const formDataObj = new FormData(e.target);
                  const formValues = {
                    name: formDataObj.get('name'),
                    email: formDataObj.get('email'),
                    phone: formDataObj.get('phone'),
                    country_code: formData.country_code,
                    message: formDataObj.get('message')
                  };

                  // Validate phone number
                  const phoneDigits = formValues.phone.replace(/\D/g, '');
                  if (phoneDigits.length !== 10) {
                    setPhoneError('Phone number must be exactly 10 digits');
                    setIsSubmitting(false);
                    return;
                  }

                  try {
                    const response = await submitLead(formValues, geoData, 'landing_page_hero');
                    
                    handleLeadResponse(
                      response,
                      (customerId) => {
                        window.location.href = `/application/main?customerid=${customerId}`;
                      },
                      (errorMsg) => {
                        setSubmitError(errorMsg);
                        setIsSubmitting(false);
                      }
                    );
                  } catch (error) {
                    setSubmitError('An error occurred. Please try again.');
                    setIsSubmitting(false);
                  }
                }} className="space-y-3">
                  <div>
                    <Label htmlFor="hero-name" className="block text-xs font-semibold text-gray-700 mb-1">
                      Full Name *
                    </Label>
                    <Input
                      id="hero-name"
                      name="name"
                      type="text"
                      required
                      placeholder="John Doe"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Label htmlFor="hero-email" className="block text-xs font-semibold text-gray-700 mb-1">
                      Email Address *
                    </Label>
                    <Input
                      id="hero-email"
                      name="email"
                      type="email"
                      required
                      placeholder="john@example.com"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Label htmlFor="hero-phone" className="block text-xs font-semibold text-gray-700 mb-1">
                      Phone Number *
                    </Label>
                    <div className="flex gap-2">
                      <Select 
                        name="country_code" 
                        value={formData.country_code}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, country_code: value }))}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue placeholder="+1 (US/CA)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+1">+1 (US/CA)</SelectItem>
                          <SelectItem value="+44">+44 (UK)</SelectItem>
                          <SelectItem value="+91">+91 (IN)</SelectItem>
                          <SelectItem value="+61">+61 (AU)</SelectItem>
                          <SelectItem value="+49">+49 (DE)</SelectItem>
                          <SelectItem value="+33">+33 (FR)</SelectItem>
                          <SelectItem value="+81">+81 (JP)</SelectItem>
                          <SelectItem value="+86">+86 (CN)</SelectItem>
                          <SelectItem value="+82">+82 (KR)</SelectItem>
                          <SelectItem value="+55">+55 (BR)</SelectItem>
                          <SelectItem value="+52">+52 (MX)</SelectItem>
                          <SelectItem value="+39">+39 (IT)</SelectItem>
                          <SelectItem value="+34">+34 (ES)</SelectItem>
                          <SelectItem value="+31">+31 (NL)</SelectItem>
                          <SelectItem value="+46">+46 (SE)</SelectItem>
                          <SelectItem value="+41">+41 (CH)</SelectItem>
                          <SelectItem value="+65">+65 (SG)</SelectItem>
                          <SelectItem value="+971">+971 (AE)</SelectItem>
                          <SelectItem value="+27">+27 (ZA)</SelectItem>
                          <SelectItem value="+64">+64 (NZ)</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex-1">
                        <Input
                          id="hero-phone"
                          name="phone"
                          type="tel"
                          required
                          placeholder="1234567890"
                          maxLength={10}
                          onChange={handlePhoneChange}
                          className={phoneError ? 'border-red-500' : ''}
                        />
                        {phoneError && (
                          <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="hero-message" className="block text-xs font-semibold text-gray-700 mb-1">
                      Message *
                    </Label>
                    <Textarea
                      id="hero-message"
                      name="message"
                      required
                      placeholder="Tell us about your trademark needs..."
                      rows={3}
                      className="w-full resize-none"
                    />
                  </div>

                  {submitError && (
                    <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                      {submitError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={phoneError || isSubmitting}
                    className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white py-3 rounded-full font-semibold hover:shadow-lg disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    🔒 Your information is secure and confidential
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Informative Section - Image Left, Content Right */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Side - Image */}
              <div>
                <img
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40"
                  alt="Professional trademark services"
                  className="rounded-3xl shadow-2xl w-full h-full object-cover"
                  style={{ minHeight: '400px', maxHeight: '500px' }}
                />
              </div>
              
              {/* Right Side - Content */}
              <div className="space-y-6">
                <div className="inline-block">
                  <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                    Why Choose Us
                  </span>
                </div>

                <h2 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
                  Your Success is Our Priority
                </h2>
                
                <p className="text-xl text-gray-700 leading-relaxed">
                  With over a decade of experience in intellectual property law, we've helped thousands of businesses 
                  protect their most valuable assets.
                </p>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our team of expert attorneys provides personalized attention to every client, ensuring your trademark 
                  registration process is smooth, efficient, and successful. We don't just file paperwork—we build 
                  long-term partnerships focused on protecting and growing your brand.
                </p>

                <div className="grid grid-cols-2 gap-6 pt-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                        <Shield className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Proven Track Record</h3>
                      <p className="text-sm text-gray-600">98% success rate in trademark approvals</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Award className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Expert Team</h3>
                      <p className="text-sm text-gray-600">Experienced IP attorneys at your service</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-red-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Fast Processing</h3>
                      <p className="text-sm text-gray-600">Quick turnaround on all applications</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">24/7 Support</h3>
                      <p className="text-sm text-gray-600">Always here when you need us</p>
                    </div>
                  </div>
                </div>

                {/* Two Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg"
                  >
                    Register My Trademark
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    onClick={() => openChat()}
                    variant="outline"
                    className="border-2 border-red-600 text-red-600 px-8 py-3 rounded-full font-semibold hover:bg-red-50"
                  >
                    Chat Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Our Services
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">What We Offer</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive intellectual property services to protect and grow your brand
              </p>
            </div>

            <MobileSlider cols={4} gap="gap-6">
              {/* Service Card 1 */}
              <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-red-200 h-full">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-red-600/10 to-transparent rounded-bl-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">Trademark Registration</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Comprehensive trademark search and registration services to protect your brand identity nationwide.
                  </p>
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transition-all text-sm"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* Service Card 2 */}
              <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-orange-200 h-full">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-orange-600/10 to-transparent rounded-bl-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-500 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform">
                    <FileCheck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">Trademark Search</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Professional comprehensive search to ensure your trademark is available and unique before filing.
                  </p>
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="bg-gradient-to-r from-orange-600 to-red-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transition-all text-sm"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* Service Card 3 */}
              <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-red-200 h-full">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-red-600/10 to-transparent rounded-bl-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">Trademark Renewal</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Keep your trademark protection active with timely renewal services and maintenance support.
                  </p>
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transition-all text-sm"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* Service Card 4 */}
              <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-orange-200 h-full">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-orange-600/10 to-transparent rounded-bl-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-red-500 rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 transition-transform">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">Trademark Monitoring</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Protect your brand with continuous monitoring for potential trademark infringements and conflicts.
                  </p>
                  <Button
                    onClick={() => setIsPopupOpen(true)}
                    className="bg-gradient-to-r from-orange-600 to-red-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transition-all text-sm"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </MobileSlider>
          </div>
        </section>

        {/* 3-Step Process Section - Creative Design */}
        <section className="py-20 bg-gradient-to-br from-white via-orange-50/30 to-red-50/30 relative overflow-hidden">
          {/* Background Decorative Elements */}
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-20 left-10 w-72 h-72 bg-red-200 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-200 rounded-full blur-3xl"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-20">
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide shadow-lg">
                  Simple Process
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get your trademark registered in three simple steps
              </p>
            </div>

            {/* Desktop view */}
            <div className="max-w-5xl mx-auto hidden md:block">
              {/* Step 1 - Left Aligned */}
              <div className="relative mb-16 flex items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-32 h-32 bg-gradient-to-br from-red-600 to-orange-500 rounded-3xl transform rotate-6 shadow-2xl flex items-center justify-center">
                      <Search className="w-16 h-16 text-white transform -rotate-6" />
                    </div>
                    <div className="absolute -top-3 -right-3 w-12 h-12 bg-white rounded-2xl shadow-lg flex items-center justify-center border-4 border-orange-100">
                      <span className="text-2xl font-black bg-gradient-to-br from-red-600 to-orange-500 bg-clip-text text-transparent">1</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 pt-4">
                  <div className="inline-block mb-2">
                    <span className="text-red-600 font-bold text-sm uppercase tracking-wider">Step One</span>
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-gray-900">Search & Consult</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    We conduct a comprehensive trademark search and provide expert consultation on your application strategy. Our team analyzes potential conflicts and advises on the best approach.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Free Consultation</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">Comprehensive Search</span>
                  </div>
                </div>

                {/* Connecting Curved Line */}
                <div className="absolute left-16 top-32 w-0.5 h-24 bg-gradient-to-b from-orange-300 to-red-300"></div>
              </div>

              {/* Step 2 - Right Aligned */}
              <div className="relative mb-16 flex items-start gap-8 flex-row-reverse">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-32 h-32 bg-gradient-to-br from-orange-600 to-red-500 rounded-3xl transform -rotate-6 shadow-2xl flex items-center justify-center">
                      <FileText className="w-16 h-16 text-white transform rotate-6" />
                    </div>
                    <div className="absolute -top-3 -left-3 w-12 h-12 bg-white rounded-2xl shadow-lg flex items-center justify-center border-4 border-red-100">
                      <span className="text-2xl font-black bg-gradient-to-br from-orange-600 to-red-500 bg-clip-text text-transparent">2</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 pt-4 text-right">
                  <div className="inline-block mb-2">
                    <span className="text-orange-600 font-bold text-sm uppercase tracking-wider">Step Two</span>
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-gray-900">File & Submit</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    Our experienced attorneys prepare and file your application with the USPTO, ensuring all requirements are met and documentation is perfect.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">Expert Filing</span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">USPTO Certified</span>
                  </div>
                </div>

                {/* Connecting Curved Line */}
                <div className="absolute right-16 top-32 w-0.5 h-24 bg-gradient-to-b from-red-300 to-orange-300"></div>
              </div>

              {/* Step 3 - Left Aligned */}
              <div className="relative flex items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-32 h-32 bg-gradient-to-br from-red-600 to-orange-500 rounded-3xl transform rotate-6 shadow-2xl flex items-center justify-center">
                      <TrendingUp className="w-16 h-16 text-white transform -rotate-6" />
                    </div>
                    <div className="absolute -top-3 -right-3 w-12 h-12 bg-white rounded-2xl shadow-lg flex items-center justify-center border-4 border-orange-100">
                      <span className="text-2xl font-black bg-gradient-to-br from-red-600 to-orange-500 bg-clip-text text-transparent">3</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 pt-4">
                  <div className="inline-block mb-2">
                    <span className="text-red-600 font-bold text-sm uppercase tracking-wider">Step Three</span>
                  </div>
                  <h3 className="text-3xl font-bold mb-3 text-gray-900">Monitor & Protect</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    We monitor your application and handle any office actions, keeping you protected throughout the process. Your trademark is our priority.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">24/7 Monitoring</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">Full Protection</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile view - Slider */}
            <div className="md:hidden">
              <MobileSlider>
                {/* Step 1 */}
                <div className="bg-white rounded-2xl p-6 shadow-lg h-full">
                  <div className="relative mb-6 flex justify-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl transform rotate-6 shadow-xl flex items-center justify-center">
                      <Search className="w-12 h-12 text-white transform -rotate-6" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 bg-white rounded-xl shadow-lg flex items-center justify-center border-4 border-orange-100">
                      <span className="text-xl font-black bg-gradient-to-br from-red-600 to-orange-500 bg-clip-text text-transparent">1</span>
                    </div>
                  </div>
                  <div className="text-center mb-2">
                    <span className="text-red-600 font-bold text-xs uppercase tracking-wider">Step One</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">Search & Consult</h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4 text-center">
                    We conduct a comprehensive trademark search and provide expert consultation on your application strategy.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Free Consultation</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">Comprehensive Search</span>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="bg-white rounded-2xl p-6 shadow-lg h-full">
                  <div className="relative mb-6 flex justify-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-orange-600 to-red-500 rounded-2xl transform -rotate-6 shadow-xl flex items-center justify-center">
                      <FileText className="w-12 h-12 text-white transform rotate-6" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 bg-white rounded-xl shadow-lg flex items-center justify-center border-4 border-red-100">
                      <span className="text-xl font-black bg-gradient-to-br from-orange-600 to-red-500 bg-clip-text text-transparent">2</span>
                    </div>
                  </div>
                  <div className="text-center mb-2">
                    <span className="text-orange-600 font-bold text-xs uppercase tracking-wider">Step Two</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">File & Submit</h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4 text-center">
                    Our experienced attorneys prepare and file your application with the USPTO, ensuring all requirements are met.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">Expert Filing</span>
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">USPTO Certified</span>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="bg-white rounded-2xl p-6 shadow-lg h-full">
                  <div className="relative mb-6 flex justify-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl transform rotate-6 shadow-xl flex items-center justify-center">
                      <TrendingUp className="w-12 h-12 text-white transform -rotate-6" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 bg-white rounded-xl shadow-lg flex items-center justify-center border-4 border-orange-100">
                      <span className="text-xl font-black bg-gradient-to-br from-red-600 to-orange-500 bg-clip-text text-transparent">3</span>
                    </div>
                  </div>
                  <div className="text-center mb-2">
                    <span className="text-red-600 font-bold text-xs uppercase tracking-wider">Step Three</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">Monitor & Protect</h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-4 text-center">
                    We monitor your application and handle any office actions, keeping you protected throughout the process.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">24/7 Monitoring</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">Full Protection</span>
                  </div>
                </div>
              </MobileSlider>
            </div>

            <div className="text-center mt-16">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-10 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all"
              >
                Register My Trademark
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-gray-50">
          <PricingTable />
        </section>

        {/* Reviews Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-block mb-4">
                <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Client Testimonials
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join thousands of satisfied businesses who trusted us with their trademark protection
              </p>
            </div>

            {/* Desktop - Auto-scroll Slider */}
            <div className="hidden md:block relative">
              <div className="overflow-hidden">
                <div className="flex animate-scroll-continuous gap-8 hover:animation-paused">
                  {/* Triple testimonials for smooth continuous scroll */}
                  {[...Array(3)].map((_, setIndex) => (
                    <div key={setIndex} className="flex gap-8 min-w-max">
                    {/* Desktop testimonials with auto-scroll */}
                    {/* Review 1 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;Absolutely phenomenal service from start to finish! The team at Trademark Scribe navigated me through the entire trademark process with such ease and professionalism. Highly recommend!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">RJ</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Rachel Johnson</p>
                          <p className="text-sm text-gray-600">Founder, Bloom Beauty Co.</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 2 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;Best decision I made for protecting my brand! The experts here are incredibly knowledgeable and made what seemed complex feel simple. Worth every penny and more!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">MK</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Marcus Kim</p>
                          <p className="text-sm text-gray-600">CEO, NexTech Solutions</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 3 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;Their attention to detail is unmatched. They caught potential issues I never would have noticed and saved my brand from future complications. True professionals!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">AP</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Amanda Peters</p>
                          <p className="text-sm text-gray-600">Owner, Luxe Lifestyle Brand</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 4 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;Lightning-fast service with impeccable results. My trademark was registered smoothly, and the communication throughout the process was outstanding. 10/10 would recommend!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">TB</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Thomas Brooks</p>
                          <p className="text-sm text-gray-600">Director, Peak Performance Fitness</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 5 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;Having worked with other trademark services before, I can confidently say Trademark Scribe stands head and shoulders above the rest. Exceptional value and service!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">NK</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Nina Kumar</p>
                          <p className="text-sm text-gray-600">Founder, Eco Green Products</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 6 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;From initial consultation to final registration, every interaction was seamless. They truly care about protecting your brand and it shows in their work. Could not be happier!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">JL</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Jordan Lee</p>
                          <p className="text-sm text-gray-600">Managing Partner, Urban Eats</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 7 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;Their expertise in trademark law is evident in every step. They explained everything clearly and ensured I understood the process. Professional, efficient, and affordable!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">CB</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Christina Brown</p>
                          <p className="text-sm text-gray-600">Owner, Artisan Coffee House</p>
                        </div>
                      </div>
                    </div>

                    {/* Review 8 */}
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all w-96">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed italic">
                        &ldquo;What impressed me most was their proactive approach. They anticipated potential issues and addressed them before they became problems. This is how trademark services should be done!&rdquo;
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">DM</span>
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">Daniel Martinez</p>
                          <p className="text-sm text-gray-600">VP Marketing, Swift Logistics</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Desktop Navigation Dots */}
            <div className="hidden md:flex justify-center mt-12 space-x-2">
              {[...Array(8)].map((_, idx) => (
                <div
                  key={idx}
                  className="w-3 h-3 rounded-full bg-gray-300 hover:bg-gray-400 transition-colors"
                  title={`Testimonial ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Mobile - Manual Swipe Slider */}
          <div className="md:hidden">
            <div className="overflow-x-scroll snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
              <div className="flex gap-6 pb-4 min-w-max">
                {/* 8 testimonials for mobile swipe */}
                {[
                  { name: 'Rachel Johnson', role: 'Founder, Bloom Beauty Co.', content: 'Absolutely phenomenal service from start to finish! The team at Trademark Scribe navigated me through the entire trademark process with such ease and professionalism. Highly recommend!' },
                  { name: 'Marcus Kim', role: 'CEO, NexTech Solutions', content: 'Best decision I made for protecting my brand! The experts here are incredibly knowledgeable and made what seemed complex feel simple. Worth every penny and more!' },
                  { name: 'Amanda Peters', role: 'Owner, Luxe Lifestyle Brand', content: 'Their attention to detail is unmatched. They caught potential issues I never would have noticed and saved my brand from future complications. True professionals!' },
                  { name: 'Thomas Brooks', role: 'Director, Peak Performance Fitness', content: 'Lightning-fast service with impeccable results. My trademark was registered smoothly, and the communication throughout the process was outstanding. 10/10 would recommend!' },
                  { name: 'Nina Kumar', role: 'Founder, Eco Green Products', content: 'Having worked with other trademark services before, I can confidently say Trademark Scribe stands head and shoulders above the rest. Exceptional value and service!' },
                  { name: 'Jordan Lee', role: 'Managing Partner, Urban Eats', content: 'From initial consultation to final registration, every interaction was seamless. They truly care about protecting your brand and it shows in their work. Could not be happier!' },
                  { name: 'Christina Brown', role: 'Owner, Artisan Coffee House', content: 'Their expertise in trademark law is evident in every step. They explained everything clearly and ensured I understood the process. Professional, efficient, and affordable!' },
                  { name: 'Daniel Martinez', role: 'VP Marketing, Swift Logistics', content: 'What impressed me most was their proactive approach. They anticipated potential issues and addressed them before they became problems. This is how trademark services should be done!' }
                ].map((testimonial, idx) => (
                  <div key={idx} className="flex-shrink-0 w-[85vw] snap-center">
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-lg border border-gray-100 h-full min-h-[280px]">
                      <div className="flex items-center gap-1 mb-4 justify-center">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                        ))}
                      </div>
                      <p className="text-base text-gray-700 mb-6 leading-relaxed italic text-center">
                        &ldquo;{testimonial.content}&rdquo;
                      </p>
                      <div className="flex items-center gap-4 justify-center">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-orange-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">{testimonial.name.split(' ').map(n => n[0]).join('')}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-gray-900">{testimonial.name}</p>
                          <p className="text-sm text-gray-600">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Navigation Dots */}
            <div className="flex justify-center mt-6 space-x-2">
              {[...Array(8)].map((_, idx) => (
                <div
                  key={idx}
                  className="w-2 h-2 rounded-full bg-gray-300"
                  title={`Testimonial ${idx + 1}`}
                />
              ))}
            </div>
          </div>
          </div>
        </section>

        {/* FAQs Section - Image Left, Content Right */}
        <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Image and Info */}
              <div className="relative">
                <div className="text-center lg:text-left mb-8 lg:hidden">
                  <div className="inline-block mb-4">
                    <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                      FAQ
                    </span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Frequently Asked Questions</h2>
                </div>

                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80" 
                    alt="Professional consultation"
                    className="w-full h-full object-cover"
                    style={{ minHeight: '500px', maxHeight: '600px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
                  
                  {/* Stats Overlay */}
                  <div className="absolute bottom-8 left-8 right-8">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-3xl font-bold text-white mb-1">98%</div>
                          <div className="text-sm text-gray-300">Success Rate</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-white mb-1">5K+</div>
                          <div className="text-sm text-gray-300">Happy Clients</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-white mb-1">24/7</div>
                          <div className="text-sm text-gray-300">Support</div>
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-white mb-1">15+</div>
                          <div className="text-sm text-gray-300">Years Experience</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-red-600 to-orange-500 rounded-full blur-2xl opacity-50"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-orange-600 to-red-500 rounded-full blur-2xl opacity-50"></div>
              </div>

              {/* Right Side - FAQs */}
              <div>
                <div className="hidden lg:block mb-12">
                  <div className="inline-block mb-4">
                    <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                      FAQ
                    </span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white leading-tight">Frequently Asked Questions</h2>
                  <p className="text-xl text-gray-300">
                    Get answers to the most common questions about trademark registration
                  </p>
                </div>

                <div className="space-y-4">
                  {/* FAQ 1 */}
                  <details className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                    <summary className="flex items-center justify-between p-6 cursor-pointer">
                      <h3 className="text-lg font-bold text-white pr-8">How long does trademark registration take?</h3>
                      <ChevronDown className="w-6 h-6 text-red-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                      The entire trademark registration process typically takes 8-12 months from filing to approval. This includes the initial review by the USPTO, publication period, and final registration. We keep you updated throughout the entire process.
                    </div>
                  </details>

                  {/* FAQ 2 */}
                  <details className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                    <summary className="flex items-center justify-between p-6 cursor-pointer">
                      <h3 className="text-lg font-bold text-white pr-8">What's included in your trademark services?</h3>
                      <ChevronDown className="w-6 h-6 text-red-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                      Our comprehensive service includes a complete trademark search, expert consultation, application preparation and filing, USPTO correspondence monitoring, office action responses, and ongoing support until your trademark is registered.
                    </div>
                  </details>

                  {/* FAQ 3 */}
                  <details className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                    <summary className="flex items-center justify-between p-6 cursor-pointer">
                      <h3 className="text-lg font-bold text-white pr-8">Do I really need a trademark?</h3>
                      <ChevronDown className="w-6 h-6 text-red-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                      Yes! A registered trademark provides nationwide protection for your brand, prevents others from using similar names, adds value to your business, and gives you legal rights to enforce your brand identity. It's an essential asset for any serious business.
                    </div>
                  </details>

                  {/* FAQ 4 */}
                  <details className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                    <summary className="flex items-center justify-between p-6 cursor-pointer">
                      <h3 className="text-lg font-bold text-white pr-8">What is your success rate?</h3>
                      <ChevronDown className="w-6 h-6 text-red-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                      We maintain an industry-leading 98% success rate for trademark applications. Our experienced attorneys conduct thorough preliminary searches and prepare applications meticulously to maximize approval chances.
                    </div>
                  </details>

                  {/* FAQ 5 */}
                  <details className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all">
                    <summary className="flex items-center justify-between p-6 cursor-pointer">
                      <h3 className="text-lg font-bold text-white pr-8">Can I trademark a business name?</h3>
                      <ChevronDown className="w-6 h-6 text-red-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                      Yes! You can trademark your business name, logo, slogan, or any distinctive element that identifies your brand. We'll help you determine the best trademark strategy for your specific business needs.
                    </div>
                  </details>
                </div>

                <div className="mt-8">
                  <p className="text-white text-lg">
                    <button 
                      onClick={() => setIsPopupOpen(true)}
                      className="underline hover:text-orange-400 transition-colors cursor-pointer font-semibold"
                    >
                      Have More Questions?
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section - Updated Background & Content */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" 
              alt="Modern city skyline at dusk"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-900/90 to-slate-900/95"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-red-600/15 to-orange-600/15"></div>
            {/* Animated overlay */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(239,68,68,0.1),transparent_70%)] animate-pulse"></div>
          </div>

          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block mb-6">
                <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide shadow-lg animate-pulse">
                  🔥 Act Now - Special Offer
                </span>
              </div>
              
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Your Brand Deserves Bulletproof Legal Protection
              </h2>
              
              <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
                Stop risking your business identity. Start protecting what you've built.
              </p>
              
              <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
                Partner with 5,000+ smart entrepreneurs who secured their trademarks with us. Receive professional legal guidance, 
                comprehensive brand protection, and the confidence that your intellectual property assets are legally safeguarded.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-10 py-6 rounded-full text-lg font-bold hover:shadow-2xl hover:scale-105 transition-all shadow-xl"
                >
                  Register My Trademark
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => openChat()}
                  variant="outline"
                  className="border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-indigo-900 transition-all"
                >
                  Talk to Experts
                </Button>
              </div>

              <div className="flex flex-wrap justify-center gap-8 text-white/90">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Shield className="w-5 h-5 text-red-400" />
                  <span className="text-sm font-medium">98% Approval Rate</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <FileCheck className="w-5 h-5 text-orange-400" />
                  <span className="text-sm font-medium">5,000+ Brands Protected</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <CheckCircle className="w-5 h-5 text-red-400" />
                  <span className="text-sm font-medium">Elite Legal Team</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Clock className="w-5 h-5 text-orange-400" />
                  <span className="text-sm font-medium">Same-Day Response</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Simple Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-400">
                © 2026 Trademark Scribe. All rights reserved.
              </div>
              <div className="flex items-center space-x-6 text-sm">
                <button onClick={() => {}} className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </button>
                <span className="text-gray-600">|</span>
                <button onClick={() => {}} className="text-gray-400 hover:text-white transition-colors">
                  Terms of Service
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default LandingPage;
